<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div>
    <span v-if="title" class="version-section-title">{{
      title.toUpperCase()
    }}</span>

    <div class="version-layout">
      <!-- Left column: Version list -->
      <div class="version-list-column">
        <ul class="version-list-container">
          <li
            v-for="version in versions"
            :key="version.id"
            class="version-row"
            :class="{
              'active-version':
                selectedVersion && selectedVersion.id === version.entry.id,
            }"
            @click="selectVersion(version.entry)"
          >
            <!-- Bubble with version number -->
            <div class="version-bubble">
              <span class="version-bubble-text">{{ version.entry.id }}</span>
            </div>

            <!-- Version information -->
            <span class="version-info">
              {{ toLocaleDateString(version.entry.modifiedAt) }} {{ $t("at") }}
              {{ toLocaleTimeString(version.entry.modifiedAt) }}
              <span class="version-author">
                {{ $t("by") }} {{ version.entry.modifiedByUser.displayName }}
                <br />
                <em v-if="version.entry.versionComment">{{
                  version.entry.versionComment
                }}</em>
              </span>
            </span>

            <!-- Download button -->
            <div class="version-actions">
              <PrimeButton
                type="button"
                icon="ri-download-line"
                :aria-label="$t('download')"
                v-tooltip.left="$t('download')"
                @click.stop="download(version.entry)"
                severity="secondary"
                rounded
                text
              />
              <PrimeButton
                type="button"
                icon="ri-history-line"
                :aria-label="$t('restore')"
                v-tooltip.left="$t('restore')"
                @click="revert(version.entry)"
                severity="secondary"
                rounded
                text
              />
            </div>
          </li>
        </ul>
      </div>

      <!-- Right column: Content viewer -->
      <div class="version-content-column">
        <PrimeCard class="version-card">
          <template #title>{{ $t("config.versionViewer.title") }}</template>
          <template #content>
            <div v-if="isContentLoading" class="version-loading">
              <PrimeProgressSpinner />
            </div>
            <div v-else-if="selectedVersionContent">
              <PrimeTextarea
                :model-value="selectedVersionContent"
                readonly
                class="version-textarea"
              />
            </div>
            <div v-else class="version-empty">
              <i class="ri-arrow-left-s-line ri-2x" aria-hidden="true"></i>
              <p>{{ $t("config.versionViewer.selectPrompt") }}</p>
            </div>
          </template>
        </PrimeCard>
      </div>
    </div>
  </div>
</template>

<script>
import Button from "primevue/button";
import Card from "primevue/card";
import Textarea from "primevue/textarea";
import ProgressSpinner from "primevue/progressspinner";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";

export default {
  name: "ListVersionConfig",
  components: {
    PrimeButton: Button,
    PrimeCard: Card,
    PrimeTextarea: Textarea,
    PrimeProgressSpinner: ProgressSpinner,
  },
  props: {
    currentNode: { type: Object, required: true },
    versions: { type: Array, default: () => [] },
    title: { type: String, default: "" },
  },
  emits: ["reload"],
  data() {
    return {
      selectedVersion: null,
      selectedVersionContent: "",
      isContentLoading: false,
    };
  },
  methods: {
    /**
     * Selects a version, triggers loading, and displays its content.
     */
    async selectVersion(version) {
      if (this.selectedVersion && this.selectedVersion.id === version.id) {
        return;
      }

      this.selectedVersion = version;
      this.isContentLoading = true;
      this.selectedVersionContent = "";

      try {
        const blob = await this.getContent(this.currentNode.id, version.id);

        const text = await blob.text();
        const contentObject = JSON.parse(text);

        this.selectedVersionContent = JSON.stringify(contentObject, null, 2);
      } catch (err) {
        this.selectedVersionContent = this.$t("config.versionViewer.loadError");
      } finally {
        this.isContentLoading = false;
      }
    },

    /**
     * Triggers the download of a specific version's content.
     */
    download(version) {
      this.getContent(this.currentNode.id, version.id, false).then((blob) => {
        const fileName = this.currentNode.name;
        const fileNameParts = fileName.split(".");
        const fileExtension = fileNameParts.pop();
        const fileNameWithoutExtension = fileNameParts.join(".");
        const downloadLink = document.createElement("a");
        const versionNb = version.id.replace(".", "-");
        downloadLink.href = URL.createObjectURL(blob);
        downloadLink.download = `${fileNameWithoutExtension}_v${versionNb}.${fileExtension}`;
        downloadLink.click();
      });
    },

    /**
     * Triggers the revert of a specific version
     */
    revert(version) {
      if (!this.versions || this.versions.length === 0) {
        console.error("Le tableau des versions est vide.");
        return;
      }
      const latestVersion = this.versions[0].entry;
      if (latestVersion.id === version.id) {
        this.$toast.add({
          severity: "warn",
          summary: this.$t("error"),
          detail: this.$t("config.versionViewer.cannotRevertLatest"),
          life: 3000,
        });
        return;
      }
      const comment = this.$t("config.versionViewer.restoredVersionComment", {
        versionNumber: version.id,
      });
      alfrescoNodeService
        .revertVersion(this.currentNode.id, version.id, comment)
        .then(() => {
          this.$emit("reload");
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("config.versionViewer.success"),
            life: 3000,
          });
        })
        .catch((err) => {
          console.error(err);
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message || this.$t("config.versionViewer.error"),
            life: 3000,
          });
        });
    },

    /**
     * Retrieves the content of a specific version through the Alfresco service.
     */
    getContent(nodeId, versionId) {
      return alfrescoNodeService
        .getVersionContent(nodeId, versionId)
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("error"),
            detail: err.message || this.$t("config.versionViewer.genericError"),
            life: 3000,
          });
          return Promise.reject(err);
        });
    },

    // Date formatting methods
    toLocaleDateString(datestr) {
      if (!datestr) {
        return "";
      }
      let date = new Date(datestr);
      return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium" }).format(
        date,
      );
    },
    toLocaleTimeString(datestr) {
      if (!datestr) {
        return "";
      }
      let date = new Date(datestr);
      return date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
  },
};
</script>

<style scoped>
.version-section-title {
  display: block;
  color: var(--p-text-muted-color);
  font-weight: 500;
  margin-bottom: 1rem;
}

.version-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 768px) {
  .version-layout {
    grid-template-columns: 5fr 7fr;
  }
}

.version-list-container {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem 0;
  max-height: 60vh;
  overflow-y: auto;
}

.version-row {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  padding-right: 0.5rem;
  cursor: pointer;
  transition: background-color 0.2s;
  border-radius: 6px;
  border-bottom: 1px solid var(--p-content-border-color);
}

.version-row:hover {
  background-color: var(--p-surface-hover);
}

.active-version {
  background-color: color-mix(
    in srgb,
    var(--p-primary-color) 10%,
    transparent
  ) !important;
  color: var(--p-primary-color);
  font-weight: bold;
}

.version-bubble {
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: color-mix(in srgb, var(--p-primary-color) 15%, transparent);
  border-radius: 50%;
  margin-right: 0.75rem;
  flex-shrink: 0;
}

.version-bubble-text {
  font-size: 1.125rem;
  color: var(--p-primary-color);
  font-weight: 700;
}

.version-info {
  color: var(--p-text-color);
  line-height: 1.5;
  flex: 1;
}

.version-author {
  color: var(--p-text-muted-color);
}

.version-actions {
  margin-left: 0.5rem;
  flex-shrink: 0;
}

.version-card {
  height: 100%;
}

.version-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 10rem;
}

.version-textarea {
  width: 100%;
  font-family: monospace;
  height: 25rem;
}

.version-empty {
  text-align: center;
  color: var(--p-text-muted-color);
}
</style>
